/* eslint-disable complexity */
import { Box, Chip, Stack, Typography } from '@mui/material'
import { Icon, Image } from '@media-components'
import { BaselineStyleClasses, CustomStyles, getStyles } from '@utils/styles'
import { ActionButton } from '../ActionButton'
import { CardData } from '../Cards'
import { SimpleDocumentRenderer } from '../DocumentRenderer'
import defaultStyles, { StylesClassNames } from './styles'

export type StackCardData = CardData

export type StackCardProps = {
  isReverse?: boolean
  csx?: CustomStyles<StylesClassNames>
  darkBg?: boolean
} & StackCardData

export function StackCard({
  title,
  description,
  points,
  imageData,
  isReverse = false,
  documentData,
  chipsData,
  actionButtonData,
  csx,
  darkBg = false,
}: StackCardProps) {
  const styles = getStyles<StylesClassNames>(defaultStyles, csx)

  return (
    <Stack
      direction={{ xs: 'column', md: isReverse ? 'row' : 'row-reverse' }}
      spacing={5}
      {...styles('wrapper', {
        backgroundColor: darkBg ? 'grey.900' : 'background.paper',
        ...(!darkBg && {
          border: '1px solid',
          borderColor: 'border.light',
        }),
      })}
      alignItems="center"
      justifyContent="space-between"
      className={BaselineStyleClasses.imageTransitioningWrapper}
    >
      <Stack spacing={5} flex={1} justifyContent="center">
        <Stack spacing={2} justifyContent="center">
          {chipsData && chipsData[0] && (
            <Box>
              <Chip
                label={
                  <Typography variant="body3.500">
                    {chipsData[0].label}
                  </Typography>
                }
                color="success"
                variant="trans"
              />
            </Box>
          )}
          {title && (
            <Typography
              variant="h6.500"
              color={darkBg ? 'text.primaryDark' : 'text.primary'}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              variant="body1.500"
              color={darkBg ? 'text.secondaryDark' : 'text.secondary'}
            >
              {description}
            </Typography>
          )}
          {documentData && !!documentData.document.length && (
            <SimpleDocumentRenderer
              document={documentData.document}
              showTickMarks
              sx={{
                marginTop: '0px !important',
                marginBottom: '0px !important',
                color: darkBg ? 'text.primaryDark' : 'text.primary',
              }}
              listSx={{
                marginTop: '24px !important',
                color: darkBg ? 'text.primaryDark' : 'text.primary',
              }}
            />
          )}
          {actionButtonData && (
            <ActionButton
              {...actionButtonData}
              size="medium"
              variant="outlined"
              type="submit"
              darkTheme={darkBg}
              showArrow
              noAnimation
            />
          )}
        </Stack>
        {points && points.length > 0 && (
          <Stack spacing={3}>
            {points.map((point, index) => (
              <Box key={index} {...styles('listItem')}>
                <Icon
                  iconName="TickCircle"
                  colorValues="green.700"
                  type="solid"
                  currentValue={false}
                />
                <Typography variant="body3.500" color="text.primaryDark">
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
      {imageData && (
        <Image
          width={393}
          height={240}
          sx={styles('image').sx}
          {...imageData}
          className={BaselineStyleClasses.imageTransition}
        />
      )}
    </Stack>
  )
}
